import { useState, useCallback, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';

/**
 * Custom hook to track Google Map viewport (bounds) and zoom level
 *
 * @param {google.maps.Map} map - Google Maps instance
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 500ms)
 * @returns {Object} { viewport, zoom, updateViewport }
 */
export const useMapViewport = (map, debounceMs = 500) => {
  const [viewport, setViewport] = useState(null);
  const [zoom, setZoom] = useState(null);

  /**
   * Get current viewport bounds from map
   * Returns bounds in format: { neLat, neLng, swLat, swLng }
   */
  const getCurrentViewport = useCallback(() => {
    if (!map) return null;

    const bounds = map.getBounds();
    if (!bounds) return null;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return {
      neLat: ne.lat(),
      neLng: ne.lng(),
      swLat: sw.lat(),
      swLng: sw.lng(),
    };
  }, [map]);

  /**
   * Get current zoom level from map
   */
  const getCurrentZoom = useCallback(() => {
    if (!map) return null;
    return map.getZoom();
  }, [map]);

  /**
   * อ่านค่า viewport ปัจจุบันแล้วเก็บลง state ทันที (ไม่ผ่าน debounce)
   */
  const applyViewport = useCallback(() => {
    const newViewport = getCurrentViewport();
    const newZoom = getCurrentZoom();

    if (newViewport && newZoom !== null && newZoom !== undefined) {
      setViewport(newViewport);
      setZoom(newZoom);
    }
  }, [getCurrentViewport, getCurrentZoom]);

  /**
   * ตัว debounce ต้องมีตัวตนคงที่ (useMemo) — เดิมสร้างใหม่ทุก render ทำให้ effect ข้างล่าง
   * ถูก cleanup แล้วเรียก .cancel() ทิ้งเรื่อย ๆ ตอนหน้าเพิ่งโหลด การอัปเดต viewport ครั้งแรก
   * จึงโดนยกเลิกทุกครั้ง viewport ค้างเป็น null และหมุดไม่ขึ้นจนกว่าผู้ใช้จะขยับแผนที่เอง
   */
  const updateViewport = useMemo(
    () => debounce(applyViewport, debounceMs),
    [applyViewport, debounceMs]
  );

  /**
   * Set up map event listeners
   */
  useEffect(() => {
    if (!map) return;

    // Listen for 'idle' event - fires when map finishes panning/zooming
    const idleListener = map.addListener('idle', () => {
      updateViewport();
    });

    // ครั้งแรกอ่านค่าทันทีแบบไม่ debounce (ถ้า bounds ยังไม่พร้อม เดี๋ยว idle ตัวแรกจะตามมาเอง)
    applyViewport();

    // Cleanup
    return () => {
      if (window.google && window.google.maps && window.google.maps.event) {
        window.google.maps.event.removeListener(idleListener);
      }
      updateViewport.cancel();
    };
  }, [map, updateViewport, applyViewport]);

  return { viewport, zoom, updateViewport };
};

export default useMapViewport;
